import React from 'react';

import { createStyles, Grid, Icon, IconButton, makeStyles, TextField, Tooltip } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { OrganogramModel } from '../../../../Models/OrganogramModel';
import { EditAction } from '../../../../shared/enum/EditAction';
import { ClientContext } from '../../ClientContext';
import { ClientOrganogramModal } from './ClientOrganogramModal';
import { BasicConfirmDialog } from '../../../../shared/components/BasicConfirmDialog';

export interface IOrganogramItemProps {
    item: OrganogramModel,
    level: number,
    textLevel: string,
    isParent: boolean,
    expanded: boolean,
}

const useStyles = makeStyles(() => createStyles({    
    organogramRow: {
        borderBottom: '1px solid rgba(0, 0, 0, .125)',
        marginBottom: -1,
    },    
}));

export function OrganogramItem({item, level, textLevel, isParent, expanded}: IOrganogramItemProps) {    
        
    const classes = useStyles();
    const clientContext = React.useContext(ClientContext);
    const [itemLocal, setItemLocal] = React.useState<OrganogramModel>({...item});
    const [inEdit, setInEdit] = React.useState<boolean>(false);
    const [showAddOrganogramDialog, setShowAddOrganogramDialog] = React.useState<boolean>(false);
    const [showDeleteOrganogramDialog, setShowDeleteOrganogramDialog] = React.useState<boolean>(false);

    React.useEffect(() => { 
        handleEditSave(false);
    }, [itemLocal.description, itemLocal.concurrent]);
    
    const handleEditSave = (isOnClick: boolean) => {
        const list = JSON.parse(JSON.stringify(clientContext.flatOrganogram));
        const index = list.findIndex((o: OrganogramModel) => o.id === itemLocal.id);
        const organogram = itemLocal;

        if (organogram.editAction != EditAction.INSERT)
            organogram.editAction = EditAction.UPDATE;

        list[index] = organogram;

        clientContext.setFlatOrganogram(list);

        if (isOnClick)
            setInEdit(false);
    };
    
    const handleEditDiscart = () => {
        setInEdit(false);
        setItemLocal(item);
    };

    const handleDelete = () => {        
        const list = JSON.parse(JSON.stringify(clientContext.flatOrganogram));
        let currentOrganogram: OrganogramModel[] = itemLocal ? [itemLocal] : [];
        let deleteList: OrganogramModel[] = itemLocal ? [itemLocal] : [];

        while (currentOrganogram.length > 0) {
            let filter: OrganogramModel[] = [];
            currentOrganogram.forEach(organogram => {
                const found : OrganogramModel[] = list.filter((o : OrganogramModel) => o.parentId === organogram.id);
                filter = filter.concat(found);
            });

            deleteList = deleteList.concat(filter);
            currentOrganogram = filter;
        }

        deleteList.forEach(organogram => {
            const index = list.findIndex((o : OrganogramModel)  => o.id === organogram.id);

            if (organogram.editAction === EditAction.INSERT)
                list.splice(index,1);
            else
                list[index].editAction = EditAction.DELETE;
        });
                
        clientContext.setFlatOrganogram(list);
    };  

    const getEditTextField = () => {
        return (
            <TextField
                value={itemLocal.description}
                size='small'
                variant="outlined"
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => 
                    setItemLocal({ ...itemLocal, description: event.target.value})
                }
            />
        );
    };

    const getAutoCompleteConcurrentEdit = () => { 
        const concurrentData = [true, false];

        return (
            <Autocomplete
                options={concurrentData}
                value={itemLocal.concurrent}
                onChange={(event, newValue: boolean | null) => {
                    event.stopPropagation();

                    if (newValue != null)
                        setItemLocal({ ...itemLocal, concurrent: newValue});
                }}
                getOptionLabel={(option : boolean) => option ? 'Sim' : 'Não'}
                getOptionSelected={(_option, value : boolean) => value == itemLocal.concurrent}
                renderInput={(params) =>
                    <TextField {...params}
                        onClick={(e) => e.stopPropagation()}
                        size='small'
                        variant="outlined" /> }
            />
        );
    };

    const getEditActions = () => {
        return (
            <>
                <Tooltip title="Salvar">
                    <IconButton
                        size="small"
                        onClick={(event) => {
                            event.stopPropagation();
                            handleEditSave(true);
                        }}>
                        <Icon className={'material-icons-round'}>done</Icon>
                    </IconButton>
                </Tooltip>
                
                <Tooltip title="Descartar">
                    <IconButton
                        size="small"
                        onClick={(event) => {
                            event.stopPropagation();
                            handleEditDiscart();
                        }}>
                        <Icon className={'material-icons-round'}>close</Icon>
                    </IconButton>
                </Tooltip>
            </>
        );
    };

    const getResumeTableIcons = () => {
        return (
            <>
                <Tooltip title="Adicionar">
                    <IconButton
                        key={`${itemLocal.id}-add`}
                        size="small"
                        onClick={(event) => {
                            event.stopPropagation();
                            setShowAddOrganogramDialog(true);
                        }}>
                        <Icon>add</Icon>
                    </IconButton>
                </Tooltip>

                <Tooltip title="Editar">
                    <IconButton
                        key={`${itemLocal.id}-edit`}
                        size="small"
                        onClick={(event) => {
                            event.stopPropagation();
                            setInEdit(true);
                        }}>
                        <Icon>edit</Icon>
                    </IconButton>
                </Tooltip>

                <Tooltip title="Apagar">
                    <IconButton
                        key={`${itemLocal.id}-delete`}
                        size="small"
                        onClick={(event) => {
                            event.stopPropagation();
                            setShowDeleteOrganogramDialog(true);
                        }}>
                        <Icon>delete_outline</Icon>
                    </IconButton>
                </Tooltip>
            </>
        );
    };

    const getTwoLevelText = (textLevel1: string, textLevel2: string, textDefault: string) => {
        switch (level) {
        case 1:
            return textLevel1;
        case 2:
            return textLevel2;
        default:
            return textDefault;
        }
    };

    const getThreeLevelText = (textLevel1: string, textLevel2: string, textLevel3: string, textDefault: string) => {
        switch (level) {
        case 1:
            return textLevel1;
        case 2:
            return textLevel2;
        case 3:
            return textLevel3;
        default:
            return textDefault;
        }
    };

    const getItemText = () => getTwoLevelText('as área/departamentos', 'os temas', 'os subtemas');
    const getDestinationText = () => getTwoLevelText('Área/Departamento', 'Tema', 'Subtema');
    const getDeleteDialogTitle = () => getThreeLevelText('marca', 'área/departamento', 'tema', 'subtema');
    const getDeleteDialogDescription = () => getThreeLevelText('dessa marca', 'desta área', 'deste tema', 'deste subtema');

    const textLevelWithItem = `${textLevel} ${itemLocal.description} > ${getDestinationText()}`;

    return (
        <Grid item xs={12} className={classes.organogramRow}>
            <Grid 
                container 
                key={`${itemLocal.id}-${level}`}
                justify="space-between"
                alignItems="center">
                <Grid item xs={8} style={{paddingLeft: `${(level-1)*15}px`}}>
                    { isParent && (
                        <>
                            <Tooltip title="Expandir">
                                <IconButton
                                    size="small">
                                    <Icon className={'material-icons-round'}>{expanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}</Icon>
                                </IconButton>
                            </Tooltip>
                            { inEdit ? getEditTextField() : itemLocal.description}
                        </>
                    )}
                    { !isParent && (<div style={{paddingLeft: '28px'}}>{ inEdit ? getEditTextField() : itemLocal.description}</div> )}
                </Grid>

                <Grid item xs={2}>
                    { inEdit ? getAutoCompleteConcurrentEdit() : (itemLocal.concurrent ? 'Sim' : 'Não')}
                </Grid>

                <Grid item xs={2}>
                    { inEdit ? getEditActions() : getResumeTableIcons()}
                </Grid>
            </Grid>
              
            <ClientOrganogramModal
                organogramLevelText={textLevelWithItem}
                itemText={getItemText()}
                forText={`"${itemLocal.description}"`}
                destinationText={getDestinationText()}
                originOrganogram={itemLocal}                
                showAddOrganogramDialog={showAddOrganogramDialog}
                setShowAddOrganogramDialog={setShowAddOrganogramDialog}
            />

            <BasicConfirmDialog
                title={`Excluir ${getDeleteDialogTitle()}?`}
                description={`Ao excluir "${itemLocal.description}" todos os níveis de organograma abaixo 
                              ${getDeleteDialogDescription()} de serão excluídos.`}
                handleFirstButton={() => handleDelete()}
                handleSecondButton={() => setShowDeleteOrganogramDialog(false)}
                textFirstButton="Excluir"
                textSecondButton="Voltar"
                reverseButtons={true}
                open={showDeleteOrganogramDialog} 
            />
        </Grid>
    );
}
